import type { ContentfulImageType, ContentfulLinkType } from '@bedrock/core/contentful';
import { getLinkData } from '@bedrock/core/contentful';
import { Link, type LinkProps } from '@bedrock/ui/link';
import { cn } from '@bedrock/ui/utils';
import { type ContentfulPDFType } from 'lib/contentful/types';

type BaseProps = {
  link?: Omit<ContentfulLinkType, 'asset'> & {
    asset?: ContentfulImageType | ContentfulPDFType;
  };
  locale: string;
};

type UnderlineProps = BaseProps &
  Omit<LinkProps, 'href'> & {
    variant: 'underline';
  };

type DefaultProps = BaseProps &
  Omit<LinkProps, 'href' | 'variant'> & {
    variant?: never;
  };

type Props = UnderlineProps | DefaultProps;

export function ContentfulLink({
  link,
  children,
  locale,
  variant,
  leftIcon,
  rightIcon,
  size,
  containerClassName,
  contentClassName,
  className,
  ...props
}: Readonly<Props>) {
  const linkData = getLinkData(link as ContentfulLinkType | undefined, locale);
  let assetLink: string | undefined;
  if (!linkData) {
    return null;
  }
  if (link?.asset?.sysType === 'dataPdf') {
    assetLink = `https:${link.asset.pdf.file.url}`;
  } else if (link?.asset?.sysType === 'image') {
    assetLink = `https:${link.asset.image.file.url}`;
  }

  if (variant === 'underline') {
    return (
      <Link
        className={cn('patterns-contentful-link', className)}
        containerClassName={cn('patterns-contentful-link-container', containerClassName)}
        contentClassName={cn('patterns-contentful-link-content', contentClassName)}
        href={assetLink || linkData.href}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        size={size}
        target={assetLink ? '_blank' : linkData.target}
        variant={variant}
        {...props}
      >
        {children}
      </Link>
    );
  }

  return (
    <Link
      className={cn('patterns-contentful-link', className)}
      href={assetLink || linkData.href}
      target={assetLink ? '_blank' : linkData.target}
      {...props}
    >
      {children}
    </Link>
  );
}
